if (!customElements.get('design-box-variant-picker')) {
  const generateBundalId = () => {
    const length = Math.floor(Math.random() * 3) + 6;
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';

    for (let i = 0; i < length; i += 1) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return result;
  };

  const appendLineItemProperties = (formData, properties, itemIndex = null) => {
    if (!properties) return;

    Object.entries(properties).forEach(([key, value]) => {
      if (itemIndex === null) {
        formData.append(`properties[${key}]`, value);
      } else {
        formData.append(`items[${itemIndex}][properties][${key}]`, value);
      }
    });
  };

  customElements.define(
    'design-box-variant-picker',
    class DesignBoxVariantPicker extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.rowCount = Number(this.dataset.rowCount || 0);
        this.priceContainer = document.querySelector(`#price-${this.sectionId}`);
        this.productForm = document.querySelector(`#product-form-${this.sectionId}`)?.closest('product-form');
        this.submitButton = this.productForm?.querySelector('[type="submit"]');
        this.submitButtonLabel = this.submitButton?.querySelector('span');
        this.addToCartText = this.dataset.addToCartText || 'Add to cart';
        this.completeSelectionText = 'Complete Your Selection';
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.bindEvents();
        this.initSmoothAccordions();
        this.updateRowTitles();
        this.updateTotalPrice();
        this.updateSubmitState();
      }

      bindEvents() {
        this.bindVariantDeselect();

        this.addEventListener('change', (event) => {
          if (!event.target.matches('.design-box-variant-item__input')) return;
          this.updateRowTitles();
          this.updateTotalPrice();
          this.updateSubmitState();
          this.updateHiddenVariantInput();
        });

        if (this.productForm) {
          this.productForm.addEventListener('submit', this.onSubmitHandler.bind(this), true);
        }
      }

      bindVariantDeselect() {
        const getInputFromEvent = (event) => {
          const input = event.target.closest('.design-box-variant-item__input');
          if (input) return input;

          const label = event.target.closest('.design-box-variant-item__label[for]');
          if (!label) return null;

          return document.getElementById(label.getAttribute('for'));
        };

        this.addEventListener(
          'pointerdown',
          (event) => {
            const input = getInputFromEvent(event);
            if (input?.type === 'radio') {
              input._wasChecked = input.checked;
            }
          },
          true
        );

        this.addEventListener(
          'click',
          (event) => {
            const input = getInputFromEvent(event);
            if (!input || input.type !== 'radio') return;

            if (input._wasChecked) {
              event.preventDefault();
              event.stopPropagation();
              input.checked = false;
              input._wasChecked = false;
              this.updateRowTitles();
              this.updateTotalPrice();
              this.updateSubmitState();
              this.updateHiddenVariantInput();
            }
          },
          true
        );
      }

      initSmoothAccordions() {
        this.querySelectorAll('.design-box-row__accordion').forEach((details) => {
          this.setupSmoothAccordion(details, {
            groupSelector: '.design-box-row__accordion',
          });
        });

        this.querySelectorAll('.design-box-row').forEach((row) => {
          row.querySelectorAll('.design-box-inner-accordion').forEach((details) => {
            this.setupSmoothAccordion(details, {
              group: row.querySelectorAll('.design-box-inner-accordion'),
            });
          });
        });
      }

      setupSmoothAccordion(details, { groupSelector, group }) {
        const summary = details.querySelector(':scope > summary');
        const body = details.querySelector(':scope > .design-box-accordion__body');
        if (!summary || !body) return;

        if (details.open) {
          body.style.gridTemplateRows = '1fr';
        }

        summary.addEventListener('click', (event) => {
          event.preventDefault();

          const willOpen = !details.open;

          if (willOpen) {
            const siblings = group || this.querySelectorAll(groupSelector);
            siblings?.forEach((other) => {
              if (other !== details && other.open) {
                this.closeSmoothAccordion(other);
              }
            });
            this.openSmoothAccordion(details);
          } else {
            this.closeSmoothAccordion(details);
          }
        });
      }

      openSmoothAccordion(details) {
        const body = details.querySelector(':scope > .design-box-accordion__body');
        if (!body) {
          details.open = true;
          return;
        }

        details.open = true;
        body.style.gridTemplateRows = '0fr';
        requestAnimationFrame(() => {
          body.style.gridTemplateRows = '1fr';
        });
      }

      closeSmoothAccordion(details) {
        const body = details.querySelector(':scope > .design-box-accordion__body');
        if (!body) {
          details.open = false;
          return;
        }

        body.style.gridTemplateRows = '0fr';

        const onTransitionEnd = (event) => {
          if (event.target !== body) return;
          details.open = false;
          body.style.gridTemplateRows = '';
          body.removeEventListener('transitionend', onTransitionEnd);
        };

        body.addEventListener('transitionend', onTransitionEnd);
      }

      getRows() {
        return Array.from(this.querySelectorAll('.design-box-row'));
      }

      getRowSelection(row) {
        return row.querySelector('.design-box-variant-item__input:checked');
      }

      getAllSelections() {
        return this.getRows()
          .map((row) => {
            const selected = this.getRowSelection(row);
            if (!selected) return null;

            const rowTitle = row.querySelector('[data-row-title]')?.dataset.rowTitle || '';
            return {
              variantId: selected.dataset.variantId,
              variantTitle: selected.dataset.variantTitle,
              price: Number(selected.dataset.variantPrice || 0),
              available: selected.dataset.variantAvailable === 'true',
              rowTitle,
              rowIndex: row.dataset.rowIndex,
            };
          })
          .filter(Boolean);
      }

      updateRowTitles() {
        this.getRows().forEach((row) => {
          const titleEl = row.querySelector('[data-row-title]');
          if (!titleEl) return;

          const selectedCount = this.getRowSelection(row) ? 1 : 0;
          let countEl = titleEl.querySelector('.design-box-row__title-count');
          if (!countEl) {
            countEl = document.createElement('span');
            countEl.className = 'design-box-row__title-count';
            titleEl.appendChild(countEl);
          }
          countEl.textContent = ` (${selectedCount}/1)`;
        });
      }

      formatMoney(cents) {
        if (window.Shopify?.formatMoney) {
          return Shopify.formatMoney(cents, window.Shopify.money_with_currency_format || window.Shopify.money_format);
        }

        const amount = (cents / 100).toFixed(2);
        return `$${amount}`;
      }

      updateTotalPrice() {
        if (!this.priceContainer) return;

        const total = this.getAllSelections().reduce((sum, selection) => sum + selection.price, 0);
        const priceValue = this.priceContainer.querySelector('[data-design-box-price-value]');
        const regularPrice = this.priceContainer.querySelector('.price-item--regular');
        const target = priceValue || regularPrice;

        if (target) {
          target.textContent = this.formatMoney(total);
        }

        const soldOutBadge = this.priceContainer.querySelector('.price__badge-sold-out');
        const hasUnavailableSelection = this.getAllSelections().some((selection) => !selection.available);

        if (soldOutBadge) {
          soldOutBadge.classList.toggle('hidden', !hasUnavailableSelection);
        }

        this.priceContainer.classList.toggle('price--sold-out', hasUnavailableSelection);
      }

      updateSubmitState() {
        if (!this.submitButton) return;

        const selections = this.getAllSelections();
        const allRowsSelected = selections.length === this.rowCount;
        const allAvailable = selections.every((selection) => selection.available);
        const canSubmit = allRowsSelected && allAvailable;

        this.submitButton.toggleAttribute('disabled', !canSubmit);
        this.submitButton.setAttribute('aria-disabled', canSubmit ? 'false' : 'true');

        if (this.submitButtonLabel) {
          if (canSubmit) {
            this.submitButtonLabel.textContent = this.addToCartText;
          } else {
            this.submitButtonLabel.textContent = this.completeSelectionText;
          }
        }

        document
          .querySelector(`product-sticky-bar[data-section-id="${this.sectionId}"]`)
          ?.syncWithMainButton?.();
      }

      updateHiddenVariantInput() {
        const variantInput = this.productForm?.querySelector('.product-variant-id');
        const selections = this.getAllSelections();
        if (!variantInput) return;

        if (selections.length === 0) {
          variantInput.value = '';
          variantInput.disabled = true;
          return;
        }

        variantInput.value = selections[0].variantId;
        variantInput.disabled = false;
      }

      async onSubmitHandler(event) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const selections = this.getAllSelections().sort(
          (a, b) => Number(a.rowIndex) - Number(b.rowIndex)
        );
        if (selections.length !== this.rowCount) return;

        const submitButton = this.submitButton;
        const spinner = this.productForm.querySelector('.loading__spinner');
        const errorMessageWrapper = this.productForm.querySelector('.product-form__error-message-wrapper');
        const errorMessage = this.productForm.querySelector('.product-form__error-message');

        submitButton?.setAttribute('aria-disabled', 'true');
        submitButton?.classList.add('loading');
        spinner?.classList.remove('hidden');
        errorMessageWrapper?.setAttribute('hidden', true);

        const bundalId = generateBundalId();

        const items = selections.map((selection) => ({
          id: selection.variantId,
          quantity: 1,
          properties: {
            _bundal: bundalId,
            [selection.rowTitle]: selection.variantTitle,
          },
        }));

        const formData = new FormData();
        items.forEach((item, index) => {
          formData.append(`items[${index}][id]`, item.id);
          formData.append(`items[${index}][quantity]`, item.quantity);
          appendLineItemProperties(formData, item.properties, index);
        });

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const config = fetchConfig('javascript');
        delete config.headers['Content-Type'];
        config.body = formData;

        try {
          const response = await fetch(`${routes.cart_add_url}`, config);
          const result = await response.json();

          if (result.status) {
            errorMessageWrapper?.removeAttribute('hidden');
            if (errorMessage) errorMessage.textContent = result.description || result.message;
            return;
          }

          if (!this.cart) {
            window.location = window.routes?.cart_url || '/cart';
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'design-box-variant-picker',
            cartData: result,
          });

          this.cart.renderContents(result);
          if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        } catch (error) {
          console.error(error);
          errorMessageWrapper?.removeAttribute('hidden');
          if (errorMessage) errorMessage.textContent = error.message;
        } finally {
          submitButton?.classList.remove('loading');
          spinner?.classList.add('hidden');
          this.updateSubmitState();
        }
      }
    }
  );
}
